import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonialsData } from './mockData';
import { useLanguage } from '../contexts/LanguageContext';
import { tr } from '../utils/i18n';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { currentLanguage } = useLanguage();
  const l = (text) => tr(currentLanguage, text);

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonialsData.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonialsData.length - 1 : prevIndex - 1
    );
  };

  const goToTestimonial = (index) => {
    setCurrentIndex(index);
  };

  return (
    <section id="testimonials" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {l('Ce que disent')}
            <span className="block bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent mt-2">
              {l('nos clients')}
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {l('Découvrez comment OptimHub a transformé les stratégies publicitaires de nos clients et les a aidés à atteindre leurs objectifs.')}
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <Card className="border-0 shadow-2xl bg-gradient-to-br from-white to-gray-50/50 overflow-hidden">
            <CardContent className="p-12">
              {/* Quote Icon */}
              <div className="flex justify-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center">
                  <Quote className="w-8 h-8 text-blue-600" />
                </div>
              </div>

              {/* Testimonial Content */}
              <div className="text-center">
                <blockquote className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-8 font-medium">
                  "{l(testimonialsData[currentIndex].content)}"
                </blockquote>

                {/* Author Info */}
                <div className="flex items-center justify-center space-x-4">
                  <Avatar className="w-12 h-12">
                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white font-semibold">
                      {testimonialsData[currentIndex].author.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">
                      {testimonialsData[currentIndex].author}
                    </div>
                    <div className="text-gray-600 text-sm">
                      {l(testimonialsData[currentIndex].role)} • {testimonialsData[currentIndex].company}
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-center space-x-4 mt-8">
                <button
                  onClick={prevTestimonial}
                  className="w-12 h-12 bg-gray-100 hover:bg-blue-500 hover:text-white rounded-full flex items-center justify-center transition-all duration-300 group"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                {/* Dots Indicator */}
                <div className="flex space-x-2">
                  {testimonialsData.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToTestimonial(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentIndex 
                          ? 'bg-blue-500 w-8' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextTestimonial}
                  className="w-12 h-12 bg-gray-100 hover:bg-blue-500 hover:text-white rounded-full flex items-center justify-center transition-all duration-300 group"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trust Indicators */}
        <div className="text-center mt-16">
          <p className="text-gray-500 mb-8">{l('Plus de 10000 clients nous font confiance')}</p>
          <div className="flex flex-wrap items-center justify-center gap-12 mb-8">
            <div className="text-gray-400 font-semibold text-lg">Amazon</div>
            <div className="text-gray-400 font-semibold text-lg">Home Depot</div>
            <div className="text-gray-400 font-semibold text-lg">Kohl's</div>
            <div className="text-gray-400 font-semibold text-lg">Macy's</div>
          </div>
          
          {/* Partners section */}
          <div className="mt-12">
            <p className="text-gray-500 mb-6">{l('Nos partenaires technologiques')}</p>
            <div className="flex flex-wrap items-center justify-center gap-10 opacity-60">
              <div className="text-gray-400 font-semibold">Awin</div>
              <div className="text-gray-400 font-semibold">TradeDoubler</div>
              <div className="text-gray-400 font-semibold">Kelkoo</div>
              <div className="text-gray-400 font-semibold">Effinity</div>
              <div className="text-gray-400 font-semibold">Commission Junction</div>
              <div className="text-gray-400 font-semibold">ShareASale</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;